import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { Login } from './components/pages/Login/Login';
import {
  AdminDashboard,
  ApproveLeaveRequest,
  Dashboard,
  IndividualLeaveInfo,
  Logout,
  AddEmployee,
  ViewEmployee,
  AdminLeaveSummary,
  EmployeeReview,
  ScheduleReview,
  HRReviewTasks,
  SupervisorReviewTasks,
  MyGoals,
  SupervisorReview,
  AnnualLeaveAllocation,
  PublicHolidayUpload,
  AttendanceUpload,
  AttendanceDashboard,
  AttendanceEmployee,
  AttendanceEmployeeDetail,
  EmployeeAttendanceCalendar,
  MyAttendanceCalendar,
  AttendanceTrackingSettings,
  HrManagerSettings,
  MaternityLeaveAllocation,
  MyVehicles,
  VehicleSearch
} from 'components';
import {
  ProtectedRouteAdmin,
  ProtectedRouteUser,
  ProtectedRouteAttendanceTracking,
  ProtectedRouteAttendanceTrackingAdmin,
  AttendanceAdminRequired
} from 'providers';
import { LeaveSummaryDashboard } from 'components/pages/LeaveSummaryDashboard';
import { UserRoles } from 'models/enums';
import './styles/index.scss';
import { EmployeeSummaryDashboard } from 'components/pages/EmployeeSummaryDashboard';
import { AttendanceSummary } from 'components/pages/AttendanceSummary';
import { AttendanceTrackingLayout } from 'components/template/AttendanceTrackingLayout';
import LeaveApprovals from 'components/pages/LeaveApprovals';
import PastLeaveApprovals from 'components/pages/PastLeaveApprovals';
import AllLeaveApprovals from 'components/pages/AllLeaveApprovals';
import { EmployeeGoalManagement } from 'components/pages/EmployeeGoalManagement';
import {
  ManagerPortalLayout,
  ManagerAttendance,
  ManagerAttendanceDetail,
  ManagerAttendanceCalendar,
  ManagerLeaveSummary,
  ManagerLeaveApprovals,
  ManagerLeaveAllocation
} from 'components/pages/ManagerPortal';

function App() {
  return (
    <div className="App">
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />

          <Route element={<ProtectedRouteUser />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/LMS" element={<IndividualLeaveInfo />} />
            <Route path="/my-attendance" element={<MyAttendanceCalendar />} />
            <Route path="/my-goals" element={<MyGoals />} />
            <Route path="/my-vehicles" element={<MyVehicles />} />
          </Route>

          <Route element={<ProtectedRouteAttendanceTracking />}>
            <Route element={<AttendanceTrackingLayout />}>
              <Route
                path="/attendance-tracking"
                element={
                  <AttendanceAdminRequired>
                    <AttendanceDashboard />
                  </AttendanceAdminRequired>
                }
              />
              <Route
                path="/attendance-tracking/upload"
                element={<AttendanceUpload />}
              />
              <Route element={<ProtectedRouteAttendanceTrackingAdmin />}>
                <Route
                  path="/attendance-tracking/employees"
                  element={<AttendanceEmployee />}
                />
                <Route
                  path="/attendance-tracking/employees/:epfNumber"
                  element={<AttendanceEmployeeDetail />}
                />
                <Route
                  path="/attendance-tracking/employees/:epfNumber/calendar"
                  element={<EmployeeAttendanceCalendar />}
                />
                <Route
                  path="/attendance-tracking/settings"
                  element={<AttendanceTrackingSettings />}
                />
                <Route
                  path="/attendance-tracking/vehicles"
                  element={<VehicleSearch />}
                />
              </Route>
            </Route>
          </Route>

          <Route
            path="/attendance-upload"
            element={<Navigate to="/attendance-tracking/upload" replace />}
          />
          <Route
            path="/admin/attendance-tracking"
            element={<Navigate to="/attendance-tracking" replace />}
          />
          <Route
            path="/admin/attendance-tracking/settings"
            element={<Navigate to="/attendance-tracking/settings" replace />}
          />

          <Route
            element={
              <ProtectedRouteAdmin
                allowedRoles={[UserRoles.MANAGER, UserRoles.ADMIN]}
              />
            }
          >
            <Route element={<ManagerPortalLayout />}>
              <Route
                path="/manager"
                element={<Navigate to="/manager/attendance" replace />}
              />
              <Route path="/manager/attendance" element={<ManagerAttendance />} />
              <Route
                path="/manager/attendance/:epfNumber"
                element={<ManagerAttendanceDetail />}
              />
              <Route
                path="/manager/attendance/:epfNumber/calendar"
                element={<ManagerAttendanceCalendar />}
              />
              <Route
                path="/manager/leave-summary"
                element={<ManagerLeaveSummary />}
              />
              <Route
                path="/manager/leave-approvals"
                element={<ManagerLeaveApprovals />}
              />
              <Route
                path="/manager/leave-allocation"
                element={<ManagerLeaveAllocation />}
              />
            </Route>
          </Route>

          <Route
            element={
              <ProtectedRouteAdmin
                allowedRoles={[UserRoles.MANAGER, UserRoles.ADMIN]}
              />
            }
          >
            <Route path="/leave-approvals" element={<LeaveApprovals />} />
          </Route>

          <Route
            element={
              <ProtectedRouteAdmin
                allowedRoles={[UserRoles.MANAGER, UserRoles.ADMIN]}
              />
            }
          >
            <Route
              path="/admin/all-leave-approvals"
              element={<AllLeaveApprovals />}
            />
          </Route>

          <Route
            element={
              <ProtectedRouteAdmin
                allowedRoles={[UserRoles.MANAGER, UserRoles.ADMIN]}
              />
            }
          >
            <Route
              path="/respondLeaveRequest"
              element={<ApproveLeaveRequest />}
            />
          </Route>

          <Route
            element={
              <ProtectedRouteAdmin
                allowedRoles={[UserRoles.ADMIN, UserRoles.TEAM_LEAD]}
              />
            }
          >
            <Route
              path="/leave-approvals/past"
              element={<PastLeaveApprovals />}
            />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/add-employee" element={<AddEmployee />} />
            <Route path="/admin/edit-employee/:id" element={<AddEmployee />} />
            <Route path="/admin/view-employee/:id" element={<ViewEmployee />} />
            <Route path="/admin/employee-review" element={<EmployeeReview />} />
            <Route
              path="/admin/employee-review/:id/goals"
              element={<EmployeeGoalManagement />}
            />
            <Route path="/admin/schedule-review" element={<ScheduleReview />} />
            <Route
              path="/admin/schedule-review/:id/tasks"
              element={<HRReviewTasks />}
            />
            <Route
              path="/admin/annual-leave-allocation"
              element={<AnnualLeaveAllocation />}
            />
            <Route
              path="/admin/maternity-leave-allocation"
              element={<MaternityLeaveAllocation />}
            />
            <Route
              path="/admin/public-holiday-upload"
              element={<PublicHolidayUpload />}
            />
            <Route
              path="/admin/hr-manager"
              element={<HrManagerSettings />}
            />
          </Route>

          <Route>
            <Route path="/supervisor-review" element={<SupervisorReview />} />
            <Route
              path="/supervisor-review/:id"
              element={<SupervisorReview />}
            />
            <Route
              path="/supervisor-review/:id/tasks"
              element={<SupervisorReviewTasks />}
            />
          </Route>

          <Route
            element={
              <ProtectedRouteAdmin
                allowedRoles={[
                  UserRoles.MANAGER,
                  UserRoles.TEAM_LEAD,
                  UserRoles.ADMIN
                ]}
              />
            }
          >
            <Route
              path="/admin/leave-summary"
              element={<AdminLeaveSummary />}
            />
            <Route
              path="/admin/leave-summary-dashboard"
              element={<LeaveSummaryDashboard />}
            />
            <Route
              path="/admin/employee-summary-dashboard"
              element={<EmployeeSummaryDashboard />}
            />
            <Route
              path="/admin/employee-attendance"
              element={<AttendanceSummary />}
            />
          </Route>
        </Routes>
      </Router>
    </div>
  );
}

export default App;
